'use client';

import { useState, useEffect } from 'react';
import { getLocalStorageItem, setLocalStorageItem } from '../lib/util';

const EarningsPopup = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        // Check if user has already seen the popup
        const hasSeenPopup = getLocalStorageItem('earnings-popup-seen');

        if (!hasSeenPopup) {
            // Show popup after a short delay for better UX
            const timer = setTimeout(() => {
                setIsVisible(true);
            }, 2000);

            return () => clearTimeout(timer);
        }

        setIsLoaded(true);
    }, []);

    const handleClose = () => {
        setIsVisible(false);
        // Remember that user has seen the popup
        setLocalStorageItem('earnings-popup-seen', 'true');
    };

    const handleGetStarted = () => {
        handleClose();
        // Scroll to trending venues or search section
        const trendingSection = document.querySelector('[data-section="trending-venues"]');
        if (trendingSection) {
            trendingSection.scrollIntoView({ behavior: 'smooth' });
        }
    };

    if (!isVisible && !isLoaded) {
        return null;
    }

    if (!isVisible) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black bg-opacity-50"
                onClick={handleClose}
            />

            {/* Popup */}
            <div className="relative bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
                {/* Close button */}
                <button
                    onClick={handleClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Close popup"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {/* Content */}
                <div className="text-center">
                    {/* Header */}
                    <div className="mb-4">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                            <span className="text-2xl">💰</span>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            Earn up to $100 on your event!
                        </h2>
                        <p className="text-gray-600">
                            Two easy ways to earn money back on your next Litty booking
                        </p>
                    </div>

                    {/* Earning methods */}
                    <div className="space-y-4 mb-6">
                        <div className="text-left p-4 bg-green-50 rounded-lg border border-green-200">
                            <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0 w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                                    1
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-1">
                                        Use promo code & earn $50
                                    </h3>
                                    <p className="text-sm text-gray-700">
                                        Check out with code <span className="font-mono bg-green-100 px-2 py-1 rounded text-green-800">IGOTLITTY</span> to earn $50 back if you invite 10 friends to your event page.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="text-left p-4 bg-purple-50 rounded-lg border border-purple-200">
                            <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0 w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                                    2
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-1">
                                        Post & tag us for another $50
                                    </h3>
                                    <p className="text-sm text-gray-700">
                                        Post on Instagram (@getlittyny) or TikTok (@getlittynyc) at your event and tag us
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CTA buttons */}
                    <div className="space-y-3">
                        <button
                            onClick={handleGetStarted}
                            className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                        >
                            Get Started
                        </button>
                        <button
                            onClick={handleClose}
                            className="w-full text-gray-500 hover:text-gray-700 font-medium py-2 transition-colors"
                        >
                            Maybe later
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EarningsPopup; 